import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, PaperPlaneTilt, EnvelopeSimple, Buildings, ShieldCheck } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

const DEFAULT_PORTS = { ssl: 465, starttls: 587, none: 25 };
const EMPTY_SMTP = { host: "", port: 465, security: "ssl", user: "", password: "", from_email: "", from_name: "" };

function TransportCard({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`flex-1 text-left rounded-lg border p-4 transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/40"}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} weight="duotone" className={active ? "text-primary" : "text-muted-foreground"} />
        <p className="text-sm font-semibold">{title}</p>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{desc}</p>
    </button>
  );
}

function StatusStrip({ s }) {
  const smtpActive = s.active_transport === "smtp";
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid="email-sending-status">
      <span className="text-[11px] text-muted-foreground">Currently sending via</span>
      <Badge variant={smtpActive ? "default" : "secondary"} data-testid="email-active-transport">
        {smtpActive ? `your mailbox · ${s.smtp.host}` : `Fixlr relay · ${s.platform_sender}`}
      </Badge>
      {s.verified_at ? (
        <Badge variant="outline" className="gap-1 text-emerald-700 border-emerald-300" data-testid="email-verified-badge">
          <CheckCircle size={12} weight="fill" /> Verified {fmtDate(s.verified_at)}
        </Badge>
      ) : (
        <Badge variant="outline" className="text-muted-foreground" data-testid="email-unverified-badge">Not tested yet</Badge>
      )}
      {s.last_error && (
        <span className="flex items-center gap-1 text-[11px] text-red-600" data-testid="email-last-error">
          <WarningCircle size={13} weight="fill" /> {s.last_error}
        </span>
      )}
      {s.fallback_count > 0 && (
        <span className="text-[11px] text-amber-700" data-testid="email-fallback-count">
          Fell back to Fixlr {s.fallback_count}× {s.last_fallback_at ? `(last ${fmtDate(s.last_fallback_at)})` : ""}
        </span>
      )}
    </div>
  );
}

export default function EmailSendingSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [replyTo, setReplyTo] = useState("");
  const [transport, setTransport] = useState("platform");
  const [fallback, setFallback] = useState(true);
  const [smtp, setSmtp] = useState(EMPTY_SMTP);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState(user?.email || "");
  const [testResult, setTestResult] = useState(null);

  const applyServer = (data) => {
    setSettings(data);
    setReplyTo(data.reply_to || "");
    setTransport(data.transport || "platform");
    setFallback(data.fallback_to_platform !== false);
    setSmtp({ ...EMPTY_SMTP, ...data.smtp, password: "" });
    setDirty(false);
  };

  useEffect(() => {
    API.get("/settings/email").then(({ data }) => applyServer(data)).catch(() => toast.error("Failed to load email settings"));
  }, []);

  const edit = (fn) => { fn(); setDirty(true); };
  const setSmtpField = (k, v) => edit(() => setSmtp((p) => ({ ...p, [k]: v })));
  const setSecurity = (sec) => edit(() => setSmtp((p) => {
    const isDefault = !p.port || Object.values(DEFAULT_PORTS).includes(Number(p.port));
    return { ...p, security: sec, port: isDefault ? DEFAULT_PORTS[sec] : p.port };
  }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { reply_to: replyTo, transport, fallback_to_platform: fallback, smtp: { ...smtp } };
      if (!body.smtp.password) delete body.smtp.password;
      const { data } = await API.put("/settings/email", body);
      applyServer(data);
      toast.success("Email sending settings saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save email settings");
      return false;
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (dirty && !(await save())) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await API.post("/settings/email/test", { to: testTo });
      setTestResult({ ok: true, ...data });
      toast.success(`Test e-mail sent to ${data.to}`);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Test e-mail failed" });
      toast.error("Test e-mail failed");
    } finally {
      setTesting(false);
      API.get("/settings/email").then(({ data }) => setSettings(data)).catch(() => {});
    }
  };

  if (!settings) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="email-sending-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Email Sending</h3>
        <p className="text-xs text-muted-foreground">
          How quotes, invoices, invites and notifications leave your workspace. Either way, replies go to you — never to Fixlr.
        </p>
      </div>

      <StatusStrip s={settings} />

      <div className="flex flex-col sm:flex-row gap-3">
        <TransportCard
          active={transport === "platform"}
          onClick={() => edit(() => setTransport("platform"))}
          icon={Buildings}
          title="Send via Fixlr"
          desc={`Zero setup. Mail goes out as ${settings.platform_sender} with your company name and a Reply-To of your choice.`}
          testid="email-transport-platform"
        />
        <TransportCard
          active={transport === "smtp"}
          onClick={() => edit(() => setTransport("smtp"))}
          icon={EnvelopeSimple}
          title="Use my own mailbox"
          desc="Send from your own address (Microsoft 365, Google Workspace, any SMTP). Best deliverability and full branding."
          testid="email-transport-smtp"
        />
      </div>

      <div className="space-y-4 border border-border rounded-lg p-4">
        <div>
          <Label className="text-xs font-semibold">Reply-To address</Label>
          <Input
            type="email"
            value={replyTo}
            onChange={(e) => edit(() => setReplyTo(e.target.value))}
            placeholder={settings.default_reply_to || "you@your-company.com"}
            className="mt-1"
            data-testid="email-replyto-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Where client replies land. {settings.default_reply_to ? <>Leave blank to use the workspace owner's e-mail (<code className="bg-muted px-1 rounded">{settings.default_reply_to}</code>).</> : "Leave blank to use the workspace owner's e-mail."}
          </p>
        </div>

        {transport === "smtp" && (
          <div className="space-y-4 pt-2 border-t border-border" data-testid="email-smtp-fields">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px_150px] gap-3">
              <div>
                <Label className="text-xs font-semibold">SMTP host</Label>
                <Input value={smtp.host} onChange={(e) => setSmtpField("host", e.target.value)} placeholder="smtp.office365.com" className="mt-1" data-testid="email-smtp-host-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Port</Label>
                <Input type="number" value={smtp.port} onChange={(e) => setSmtpField("port", e.target.value)} className="mt-1" data-testid="email-smtp-port-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Security</Label>
                <Select value={smtp.security} onValueChange={setSecurity}>
                  <SelectTrigger className="mt-1" data-testid="email-smtp-security-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                    <SelectItem value="none">None (25)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Username</Label>
                <Input value={smtp.user} onChange={(e) => setSmtpField("user", e.target.value)} placeholder="ops@your-company.com" className="mt-1" autoComplete="off" data-testid="email-smtp-user-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Password</Label>
                <Input
                  type="password"
                  value={smtp.password}
                  onChange={(e) => setSmtpField("password", e.target.value)}
                  placeholder={settings.smtp.has_password ? "•••••••• (saved — type to replace)" : "App password"}
                  className="mt-1"
                  autoComplete="new-password"
                  data-testid="email-smtp-password-input"
                />
                {settings.smtp.has_password && (
                  <p className="flex items-center gap-1 text-[10px] text-emerald-700 mt-1" data-testid="email-smtp-password-saved">
                    <ShieldCheck size={12} weight="fill" /> Password stored encrypted
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">From name</Label>
                <Input value={smtp.from_name} onChange={(e) => setSmtpField("from_name", e.target.value)} placeholder="Your company" className="mt-1" data-testid="email-smtp-fromname-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">From address</Label>
                <Input type="email" value={smtp.from_email} onChange={(e) => setSmtpField("from_email", e.target.value)} placeholder="Defaults to the username" className="mt-1" data-testid="email-smtp-fromemail-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Fall back to Fixlr if my mailbox fails</p>
                <p className="text-[10px] text-muted-foreground">Nothing gets lost — we send via {settings.platform_sender} and alert your admins.</p>
              </div>
              <Switch checked={fallback} onCheckedChange={(v) => edit(() => setFallback(v))} data-testid="email-fallback-toggle" />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Microsoft 365: smtp.office365.com · STARTTLS 587 · enable "Authenticated SMTP" for the mailbox. Google Workspace: smtp.gmail.com · SSL 465 · use an app password.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={saving || !dirty} data-testid="save-email-sending-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
        {dirty && <span className="text-[11px] text-amber-700" data-testid="email-unsaved-hint">Unsaved changes</span>}
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3" data-testid="email-test-card">
        <div>
          <p className="text-sm font-medium">Send a test e-mail</p>
          <p className="text-[10px] text-muted-foreground">Uses the settings above{dirty ? " (saves them first)" : ""}. Check the From and Reply-To headers when it arrives.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@your-company.com" data-testid="email-test-to-input" />
          <Button variant="outline" onClick={sendTest} disabled={testing || saving || !testTo} className="gap-2 shrink-0" data-testid="send-test-email-btn">
            <PaperPlaneTilt size={15} /> {testing ? "Sending..." : "Send test"}
          </Button>
        </div>
        {testResult && (
          testResult.ok ? (
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300" data-testid="email-test-success">
              Sent to <strong>{testResult.to}</strong> via {testResult.transport === "smtp" ? "your mailbox" : "the Fixlr relay"} as <code>{testResult.sender}</code>
              {testResult.reply_to && <> · replies go to <code>{testResult.reply_to}</code></>}
            </div>
          ) : (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2 text-xs text-red-700 dark:text-red-300" data-testid="email-test-error">
              {testResult.error}
            </div>
          )
        )}
      </div>
    </div>
  );
}
